import s from './styles.module.scss'

const COLUMN_LABELS = ['A', 'B', 'C', 'D']

/**
 * Fake cell contents as bar widths (percentages). 0 leaves the cell empty so
 * the grid reads like a partly filled sheet rather than a solid block.
 * First row is the header row, drawn darker.
 */
const CELL_ROWS = [
    [72, 58, 64, 40],
    [88, 46, 70, 0],
    [64, 76, 52, 34],
    [80, 38, 0, 44],
    [54, 68, 60, 0],
]

/** skeleton = empty placeholder sheet; filled = populated, colour sheet. */
export type DecorativeSpreadsheetVariant = 'skeleton' | 'filled'

type DecorativeSpreadsheetProps = {
    /** Filename shown on the sheet tab — hidden in the skeleton variant. */
    label?: string
    variant?: DecorativeSpreadsheetVariant
    className?: string
}

/**
 * Purely decorative miniature spreadsheet, used to illustrate the batch
 * upload card. Layers: paper gradient, a tiled noise texture multiplied on
 * top for fibre grain, then tiny inline spreadsheet furniture (title bar,
 * column headers, row numbers, cell bars) and a sheen.
 */
export const DecorativeSpreadsheet = ({
    label = 'clients.csv',
    variant = 'filled',
    className,
}: DecorativeSpreadsheetProps) => {
    const [headerRow, ...bodyRows] = CELL_ROWS
    const isSkeleton = variant === 'skeleton'

    return (
        <div
            aria-hidden="true"
            className={`${s.spreadsheet} ${isSkeleton ? s.isSkeleton : ''} ${className ?? ''}`}
        >
            <div className={s.paperBase} />
            <div className={s.paperGrain} />

            <div className={s.content}>
                <div className={s.titleBar}>
                    <span className={s.titleDots}>
                        <span className={s.dot} />
                        <span className={s.dot} />
                        <span className={s.dot} />
                    </span>
                    {isSkeleton ? <span className={s.titlePlaceholder} /> : <span className={s.title}>{label}</span>}
                </div>

                <div className={s.grid}>
                    <div className={s.columnHeaders}>
                        <span className={s.corner} />
                        {COLUMN_LABELS.map((column) => (
                            <span key={column} className={s.columnLabel}>
                                {isSkeleton ? '' : column}
                            </span>
                        ))}
                    </div>

                    <div className={`${s.row} ${s.headerRow}`}>
                        <span className={s.rowLabel}>{isSkeleton ? '' : 1}</span>
                        {headerRow.map((width, index) => (
                            <span key={index} className={s.cell}>
                                <span className={s.headerBar} style={{ width: `${width}%` }} />
                            </span>
                        ))}
                    </div>

                    {bodyRows.map((row, rowIndex) => (
                        <div key={rowIndex} className={s.row}>
                            <span className={s.rowLabel}>{isSkeleton ? '' : rowIndex + 2}</span>
                            {row.map((width, cellIndex) => (
                                <span key={cellIndex} className={s.cell}>
                                    {width > 0 && <span className={s.cellBar} style={{ width: `${width}%` }} />}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            <div className={s.sheen} />
        </div>
    )
}

export default DecorativeSpreadsheet
